import {
  AlignItems,
  AlignSelf,
  Box,
  Breakpoint,
  Color,
  Display,
  Display2,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React from 'react'

import type { OrderBoxData } from '@library/api-customer-engagement-experience'

import platesImg from '../../../../assets/plates.png'

type OrderAnotherBoxProps = {
  data: OrderBoxData
  onAction: (intent: string) => void
}

const Container = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.ColdGrey_800),
  padding: `${theme.spacing(4)}px`,
  borderRadius: '8px',
  maxWidth: '1200px',
  margin: '0 auto',
  '@media (min-width: 480px)': {
    padding: `${theme.spacing(6)}px`,
  },
}))

const ContentGrid = withCitrusTheme(Box, (theme) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'center',
  gap: `${theme.spacing(3)}px`,
}))

// Mobile/tablet: the offer sits in the top-right of the header. On desktop it moves
// out to sit between the CTA and the plates (see the row-level instance below).
const BadgeCell = withCitrusTheme(Box, () => ({
  gridColumn: '2',
  justifySelf: 'end',
  [`@media (min-width: ${Breakpoint.Large})`]: {
    display: 'none',
  },
}))

const DiscountBadge = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.Success_900),
  borderRadius: '4px',
  color: theme.color(Color.White),
  padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  flexShrink: 0,
  whiteSpace: 'nowrap',
}))

const Heading = withCitrusTheme(Display2, (theme) => ({
  color: theme.color(Color.White),
  margin: 0,
}))

const TagList = withCitrusTheme('ul', (theme) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: `${theme.spacing(2)}px`,
  color: theme.color(Color.White),
}))

const TagItem = withCitrusTheme('li', (theme) => ({
  alignItems: 'center',
  display: 'flex',
  gap: `${theme.spacing(2)}px`,
}))

const PlatesImage = withCitrusTheme('img', () => ({
  display: 'block',
  width: '100%',
  height: 'auto',
  maxWidth: '560px',
  objectFit: 'contain',
}))

const CTAButton = withCitrusTheme('button', (theme) => ({
  appearance: 'none',
  border: 'none',
  alignItems: 'center',
  backgroundColor: theme.color(Color.Secondary_400),
  borderRadius: '8px',
  color: theme.color(Color.White),
  cursor: 'pointer',
  display: 'flex',
  height: '48px',
  justifyContent: 'center',
  padding: `0 ${theme.spacing(6)}px`,
  textAlign: 'center',
  transition: 'background-color 150ms ease-in-out',
  width: '100%',
  maxWidth: '500px',
  marginLeft: 'auto',
  marginRight: 'auto',
  [`@media (min-width: ${Breakpoint.Large})`]: {
    marginLeft: 0,
  },
  '&:hover': {
    backgroundColor: theme.color(Color.Secondary_500),
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.color(Color.White)}`,
    outlineOffset: '2px',
  },
}))

const OfferBadge = ({ badge }: { badge: string }) => (
  <DiscountBadge>
    <Text fontWeight={FontWeight.Bold} size={[3, 5, 6]}>
      {badge}
    </Text>
  </DiscountBadge>
)

export const OrderAnotherBox = ({ data, onAction }: OrderAnotherBoxProps) => (
  <Container data-testid="order-another-box">
    <Box
      alignItems={[AlignItems.Stretch, null, null, AlignItems.Center]}
      display={Display.Flex}
      flexDirection={[FlexDirection.Column, null, null, FlexDirection.Row]}
      gap={[4, null, null, 6]}
    >
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        flexBasis="0"
        flexGrow={1}
        gap={3}
      >
        <ContentGrid>
          <Heading size={[6, 12, 13]}>{data.title}</Heading>
          {data.offer ? (
            <BadgeCell>
              <OfferBadge badge={data.offer.badge} />
            </BadgeCell>
          ) : null}
        </ContentGrid>

        <TagList>
          {data.tags.map((tag) => (
            <TagItem key={tag}>
              <Icon aria-hidden="true" name="tick" size={4} variant={IconVariant.Inherit} />
              <Text fontWeight={[FontWeight.Normal, FontWeight.Bold]} size={[3, 4, 5]}>
                {tag}
              </Text>
            </TagItem>
          ))}
        </TagList>

        <Box
          display={[Display.Flex, null, null, Display.None]}
          justifyContent={JustifyContent.Center}
        >
          <PlatesImage alt="" aria-hidden="true" src={platesImg} />
        </Box>

        <CTAButton
          data-testid="order-another-box-cta"
          onClick={() => onAction(data.cta.intent)}
          type="button"
        >
          <Text fontWeight={FontWeight.Bold} size={[3, 4]}>
            {data.cta.title}
          </Text>
        </CTAButton>
      </Box>

      {/* Desktop only: the offer sits between the CTA/content and the plates, top-aligned. */}
      {data.offer ? (
        <Box
          display={[Display.None, Display.None, Display.None, Display.Flex]}
          alignSelf={AlignSelf.Start}
        >
          <OfferBadge badge={data.offer.badge} />
        </Box>
      ) : null}

      <Box
        alignItems={AlignItems.Center}
        display={[Display.None, null, null, Display.Flex]}
        flexBasis="0"
        flexGrow={1}
        justifyContent={JustifyContent.FlexEnd}
      >
        <PlatesImage alt="" aria-hidden="true" src={platesImg} />
      </Box>
    </Box>
  </Container>
)
